import { HttpEventType } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { map } from 'rxjs';

import {
  faAdd,
  faCheck,
  faFile,
  faFileArrowUp,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';

import { UploadService } from './upload.service';
import { PublicFile } from '../../models';
import { ImageViewerService } from '../image-viewer/image-viewer.service';
import { NotificationService } from '../../services';

@Component({
  selector: 'canvas-admin-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UploadComponent,
    },
  ],
  standalone: false,
})
export class UploadComponent implements OnInit, ControlValueAccessor {
  constructor(
    private readonly notificationService: NotificationService,
    private readonly service: UploadService,
    private readonly imageViewerService: ImageViewerService
  ) {}

  @Input() IsMultiple = true;
  @Input() DisplayUploadBoxesAmount = 3;
  @Input() DragDropText?: string;
  @Input() IsRequired = false;
  @Input() HideDragDrop = false;

  // Defines the allowed file types for upload, such as 'image/*', 'application/pdf', or multiple types like 'image/png,image/jpeg'.
  @Input() AllowedFileTypes!: string;
  @Input() Disabled = false;
  @Input() Directory = '';
  @Input() Url = '';
  @Input() MaxFileSize = '';
  @Input() createThumbnail = false;
  @Input() thumbnailUrl = '';
  @Input() showAddButton = true;
  @Input() bypassUpload = false;

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  @Output() thumbnailUploaded = new EventEmitter<PublicFile>();
  @Output() fileAdded = new EventEmitter<File>();

  public files: PublicFile[] = [];

  public icon = faFileArrowUp;
  public addIcon = faAdd;
  public doc = faFile;
  public trash = faTrash;
  public faCheck = faCheck;
  public Hovering = false;

  selectedItemFocus = -1;
  onChange!: (val: PublicFile[]) => void;
  onTouched!: () => void;

  writeValue(obj: any): void {
    this.files = obj || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnInit(): void {
    if (!this.AllowedFileTypes) {
      this.notificationService.showError('A valid file type must be provided.');
      return;
    }

    if (this.files) {
      const length = this.files.length;

      if (this.IsMultiple) {
        const renderAmount = this.DisplayUploadBoxesAmount - length;
        if (renderAmount > 0) {
          for (let index = 0; index < renderAmount; index++) {
            this.AddNewFileForm();
          }
        }
      }
    }
  }

  get DragText(): string {
    if (!this.DragDropText)
      return (
        (this.IsMultiple ? 'Drag & drop files ' : 'Drag & drop a file ') +
        'here or click browse'
      );

    return this.DragDropText;
  }

  public DragOverHandler($event: Event): void {
    $event.preventDefault();
    this.Hovering = true;
  }

  public DropHandler($event: DragEvent): void {
    $event.preventDefault();
    this.RemoveHovering();
    if ($event.dataTransfer?.items) {
      const files = [
        ...($event.dataTransfer.items as unknown as DataTransferItem[]),
      ].map((f) => f.getAsFile() as File);

      if (this.IsMultiple) {
        this.HandleFiles(files);
      } else {
        this.HandleFile(files[0], 0, true);
      }
    }
  }

  public RemoveHovering(): void {
    setTimeout(() => (this.Hovering = false), 300);
  }

  public OpenFileDialog(index?: number, isMultiple = false): void {
    if (this.fileInput && !this.Disabled) {
      const input = this.fileInput.nativeElement as HTMLInputElement;
      input.multiple = isMultiple;
      input.accept = this.AllowedFileTypes;
      input.onchange = (ev: Event) => this.FileChanged(ev, index);
      input.click();
    }
  }

  public RemoveFile(index: number) {
    this.files.splice(index, 1);
    this.onChange(this.files);
  }

  public FileChanged($event: Event, index?: number) {
    if (this.fileInput) {
      const input = this.fileInput.nativeElement as HTMLInputElement;
      if (input.files) {
        const files = [...(input.files as unknown as File[])];
        if (index !== undefined) {
          this.HandleFile(files[0], index);
        } else {
          this.HandleFiles(files);
        }
      }
    }
  }

  public showImage(name: string, url: string) {
    this.imageViewerService.showImage(name, url);
  }

  get allFiles(): PublicFile[] {
    return this.files?.length > 0 ? this.files : [];
  }

  private HandleFile(file: File, index?: number, replace = false) {
    if (this.Disabled || (!this.Url && !this.bypassUpload)) {
      return;
    }

    const allowedFileTypes = this.AllowedFileTypes.split(',').map((type) =>
      type.trim()
    );

    const isAllowedFileType = allowedFileTypes.some((type) => {
      return (
        type === file.type ||
        (type.includes('/*') && file.type.startsWith(type.split('/')[0])) ||
        type.includes('.')
      );
    });

    if (!isAllowedFileType) {
      this.notificationService.showError(
        `Cannot add file '${file.name}' with a type of '${file.type}'`,
        'File Type Error'
      );
      return;
    }

    if (this.MaxFileSize && file.size > this.parseFileSize(this.MaxFileSize)) {
      this.notificationService.showError(
        `'${file.name}' exceeds the maximum allowed size of ${this.MaxFileSize}. Please upload a smaller file.`,
        'File Size Error'
      );
      return;
    }

    if (this.bypassUpload) {
      this.fileAdded.emit(file);
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = this.Url;

    if (this.createThumbnail) {
      this.uploadThumbnail(formData);
    }
    this.service
      .Upload(formData, url)
      .pipe(
        map((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                const progress = Math.round((event.loaded * 100) / event.total);
              }
              break;
            case HttpEventType.Response:
              return event;
          }
          return null;
        })
      )
      .subscribe((event) => {
        if (!event || typeof event !== 'object') return;
        const newFile = event.body;

        if (this.IsMultiple) {
          this.files.push(newFile as PublicFile);
        } else {
          this.files[0] = newFile as PublicFile;
        }
        this.onChange(this.files);
      });
  }

  private uploadThumbnail(formData: FormData) {
    this.service.Upload(formData, this.thumbnailUrl).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.Response) {
          this.thumbnailUploaded.emit(event.body as PublicFile);
        }
      },
    });
  }

  private HandleFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }

    files.forEach((file, index) => {
      this.HandleFile(file, index);
    });
  }

  private AddNewFileForm(value?: string, required?: boolean): void {
    this.files.push({ fileName: value, url: value } as PublicFile);
  }

  private parseFileSize(fileSize: string): number {
    const size = parseFloat(fileSize);
    if (fileSize.toLowerCase().includes('mb')) {
      return size * 1024 * 1024;
    } else if (fileSize.toLowerCase().includes('kb')) {
      return size * 1024;
    } else if (fileSize.toLowerCase().includes('gb')) {
      return size * 1024 * 1024 * 1024;
    }
    return size;
  }

  isPdf() {
    return this.AllowedFileTypes.indexOf('pdf') >= 0;
  }
}
